// 아바타 망토 형상(L1029). 본 로컬(Spine2 기준, 모델 배율 1.5 전 m): +Y 위, +Z 앞(몸 쪽), 망토는 -Z(등 쪽)에 있다.
// 어깨 폭에서 아래로 넓어지는 사다리꼴 + 등을 감싸는 원통 일부(가운데가 뒤로 볼록) + 물결 아랫단.
// 정점 130개(13열 × 10행). 프레임 루프에서는 만들지 않는다 — 장착이 바뀔 때 한 번만 만든다.
import * as THREE from 'three/webgpu';

export const CAPE_COLS = 12;
export const CAPE_ROWS = 9;
/** 위→아래 길이(m) */
export const CAPE_H = 0.6;
/** 위(칼라)·아래 반폭(m) */
export const CAPE_W_TOP = 0.15;
export const CAPE_W_BOTTOM = 0.27;
/** 어떤 정점도 이 z(본 +Z)보다 앞으로 나오지 않는다 — 몸 뚫림 방지 오프셋 */
export const CAPE_CLEAR_Z = 0.01;
/** 가운데(등 정중앙) 정점이 항상 이 z 이하(뒤)에 있다 */
export const CAPE_CENTER_MAX_Z = -0.06;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** t(0 위..1 아래) 행의 반폭 */
export function capeHalfWidth(t: number): number { return lerp(CAPE_W_TOP, CAPE_W_BOTTOM, Math.pow(t, 0.85)); }
/** t 행의 감싸는 반각(rad). 위쪽 칼라는 어깨를 많이 감싸고 아래는 펴진다 */
export function capeWrapAngle(t: number): number { return lerp(0.9, 0.5, Math.pow(t, 0.7)); }
/** t 행 가운데의 등에서 띄운 거리(아래로 갈수록 뒤로 기운다) */
export function capeBackOffset(t: number): number { return 0.07 + 0.13 * Math.pow(t, 1.1); }
/** 아랫단 물결: 홀수 열이 위로 말려 올라간다(결정적, 높이는 열마다 조금씩 다르다) */
export function capeHemLift(col: number): number { return col % 2 === 1 ? 0.045 + 0.015 * (((col * 7) % 3) / 2) : 0; }

export interface CapeShape {
  position: Float32Array;
  uv: Float32Array;
  index: Uint16Array;
  vertexCount: number;
}

export function capeShape(): CapeShape {
  const cols = CAPE_COLS, rows = CAPE_ROWS, stride = cols + 1;
  const vertexCount = stride * (rows + 1);
  const position = new Float32Array(vertexCount * 3), uv = new Float32Array(vertexCount * 2);
  const index = new Uint16Array(rows * cols * 6);
  for (let r = 0; r <= rows; r++) {
    const t = r / rows, w = capeHalfWidth(t), th = capeWrapAngle(t), d = capeBackOffset(t);
    const R = w / Math.sin(th);
    for (let c = 0; c <= cols; c++) {
      const u = c / cols * 2 - 1, a = u * th, i = r * stride + c;
      let y = -t * CAPE_H;
      // 아랫단: 마지막 행은 전부, 바로 윗 행은 3할만 따라 올려 물결 실루엣을 만든다
      if (r === rows) y += capeHemLift(c); else if (r === rows - 1) y += capeHemLift(c) * 0.3;
      position[i * 3] = R * Math.sin(a);
      position[i * 3 + 1] = y;
      position[i * 3 + 2] = -d + R * (1 - Math.cos(a));
      uv[i * 2] = c / cols;
      uv[i * 2 + 1] = t;
    }
  }
  let k = 0;
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    const i = r * stride + c;
    index[k++] = i; index[k++] = i + stride; index[k++] = i + 1;
    index[k++] = i + 1; index[k++] = i + stride; index[k++] = i + stride + 1;
  }
  return { position, uv, index, vertexCount };
}

export function buildCapeGeometry(): THREE.BufferGeometry {
  const s = capeShape();
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(s.position, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(s.uv, 2));
  g.setIndex(new THREE.BufferAttribute(s.index, 1));
  g.computeVertexNormals();
  return g;
}

/**
 * 격자 기반 정점 노멀(L1030 천 시뮬용). computeVertexNormals 대신 쓴다 — 할당 없이 매 프레임 부를 수 있다.
 * 각 정점에서 가로(열)·세로(행) 이웃 차분의 외적. 방향은 computeVertexNormals와 같다(삼각형 i, i+stride, i+1 기준).
 */
export function capeNormals(position: ArrayLike<number>, out: Float32Array): void {
  const cols = CAPE_COLS, rows = CAPE_ROWS, stride = cols + 1;
  for (let r = 0; r <= rows; r++) {
    const r0 = r > 0 ? r - 1 : r, r1 = r < rows ? r + 1 : r;
    for (let c = 0; c <= cols; c++) {
      const c0 = c > 0 ? c - 1 : c, c1 = c < cols ? c + 1 : c;
      const a = (r * stride + c0) * 3, b = (r * stride + c1) * 3, p = (r0 * stride + c) * 3, q = (r1 * stride + c) * 3;
      const ux = position[b] - position[a], uy = position[b + 1] - position[a + 1], uz = position[b + 2] - position[a + 2]; // 오른쪽
      const vx = position[q] - position[p], vy = position[q + 1] - position[p + 1], vz = position[q + 2] - position[p + 2]; // 아래쪽
      let nx = vy * uz - vz * uy, ny = vz * ux - vx * uz, nz = vx * uy - vy * ux; // v × u
      const l = Math.hypot(nx, ny, nz);
      if (l > 1e-12) { nx /= l; ny /= l; nz /= l; } else { nx = 0; ny = 0; nz = -1; }
      const o = (r * stride + c) * 3;
      out[o] = nx; out[o + 1] = ny; out[o + 2] = nz;
    }
  }
}
